<template>
    <div class="relative">
        <div v-if="haveCustomOptions !== false" class="sticky top-0 z-50 bg-white w-full h-[152px]">
            <placement-header-alt
                :placement="visiblePlacements[0]"
                :placementData="currentGroupActual"
                :index="0"
                :has-error="hasLocalErrorState"
                :hide-combine="true"
                :show-selections="true"
            >
                <div v-if="hasLocalErrorState"
                    class="text-red-error text-sm"
                >
                    {{ $t('ERROR_NO_CUSTOMIZATION') }}
                </div>
            </placement-header-alt>
            <div class="flex items-center justify-center gap-[10px] px-[20px] lg:px-[40px] border-b border-gray-DFDFDF py-[20px]">
                <black-white-button
                    :is-selected="selectedOption === 'stock'"
                    class="w-full"
                    @click="handleSelectStock"
                >
                    {{ $t('STOCK') }}
                </black-white-button>
                <black-white-button
                    :is-selected="selectedOption === 'custom'"
                    class="w-full flex items-center justify-center"
                    @click="handleSelectCustom"
                >
                    <crown-icon
                        :class="[
                            'mr-[8px]',
                            selectedOption === 'custom' ? 'text-white' : 'text-black'
                        ]"
                    />
                    {{ $t('CUSTOM') }}
                </black-white-button>
            </div>
        </div>

        <div class="pt-[29.73px]"></div>

        <div v-for="placement in visiblePlacementsNonColors"
            :key="placement.code"
            class="relative px-[20px] lg:px-[40px] flex flex-wrap justify-between"
        >
            <step-options-initials-buttons
                v-for="(option, i) in styleOptions(placement)"
                class="w-full"
                :key="option.code"
                :has-local-error="hasLocalErrorState"
                :main-placement="placement"
                :option="option"
                @unselect="handleSelectNothing(placement)"
                @select="selectActual"
                :index="i"
                :scroll-to-offset="180"
                :has-error-state="hasLocalErrorState"
                :is-full-width="false"
                :has-checkmark-on-selected="true"
                :has-zoom-in="true"
                :has-lock-icon="isLocked && selectedOption === 'custom'"
                :has-box-style-pricing="true"
            />

            <placement-standard :placement="placement"></placement-standard>
        </div>
    </div>
</template>

<script>
    import allStores from '../stores/all';
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import PlacementStandard from './PlacementStandard.vue';
    import BlackWhiteButton from './buttons/BlackWhiteButton.vue';
    import CrownIcon from './CrownIcon.vue';

    export default {
        name: 'StepOptionsGrip',

        mixins: [allStores],

        components: {
            StepOptionsInitialsButtons,
            PlacementHeaderAlt,
            PlacementStandard,
            BlackWhiteButton,
            CrownIcon,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                selectedOption: 'stock',
                haveCustomOptions: null,
            };
        },

        watch: {
            selectedOption(newVal) {
                if (newVal === 'custom' && this.isLocked) {
                    this.showLockedToast();
                }
            },
        },

        computed: {
            isLocked() {
                return this.customizerStore.isGroupLocked('Grip');
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            currentGroupActual() {
                return this.visibleGroups.find((group) => group.name === this.currentGroupName);
            },

            visiblePlacements() {
                const placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    return {
                        selectedValue: selected ? selected.code : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            visiblePlacementsNonColors() {
                return this.visiblePlacements.filter((x) => !x.code.includes('Color'));
            },

            isComplete() {
                return this.visiblePlacementsNonColors.some((placement) => !!placement.selectedValue);
            },

            hasLocalErrorState() {
                return this.isDirty && !this.isComplete;
            },
        },

        mounted() {
            // Do we have custom options?
            let haveAny = false;

            // eslint-disable-next-line no-restricted-syntax
            for (const placement of this.visiblePlacementsNonColors) {
                const custom = this.styleOptions(placement, 'custom');
                if (custom?.length > 0) {
                    haveAny = true;
                }
            }

            this.haveCustomOptions = haveAny;
        },

        methods: {
            handleSelectStock() {
                this.selectedOption = 'stock';
            },

            handleSelectCustom() {
                this.selectedOption = 'custom';
            },

            styleOptions(placement, gripType) {
                const useType = gripType ?? this.selectedOption;

                const options = this.customizerStore.sortOptions(placement, placement?.selectedValue || null);

                // On putters, only show the default assembly option and default size option.
                const filtered = [];

                // eslint-disable-next-line no-restricted-syntax
                for (const o of options) {
                    // Assume there will be only one.
                    // if (o.raw.custom['v-assembly-default'] !== 'true' || o.raw.custom['v-size-default'] !== 'true') {
                    //     // eslint-disable-next-line no-continue
                    //     continue;
                    // }

                    const isCustomShop = o.raw.custom['v-custom-shop'] === 'true';

                    if (useType === 'custom') {
                        if (!isCustomShop) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }
                    }

                    if (useType === 'stock') {
                        if (isCustomShop) {
                            // eslint-disable-next-line no-continue
                            continue;
                        }
                    }

                    filtered.push(o);
                }

                const defaultGrip = this.customizerStore.getDefaultGripFromPlacement(this.viewController, placement);

                if (!defaultGrip) {
                    return filtered;
                }

                return filtered
                    .map((option, index) => ({
                        option,
                        index,
                        isDefaultGrip: option.raw?.code === defaultGrip.code,
                    }))
                    .sort((a, b) => {
                        if (a.isDefaultGrip === b.isDefaultGrip) {
                            return a.index - b.index;
                        }

                        return a.isDefaultGrip ? -1 : 1;
                    })
                    .map(({ option }) => option);
            },

            isCustomShop(option) {
                return option.raw.custom['v-custom-shop'] === 'true';
            },

            handleSelectNothing(targetPlacement) {
                // eslint-disable-next-line no-unused-vars
                const targetPlacementCode = targetPlacement?.code;

                const placement = this.viewController.findPlacement(targetPlacementCode);
                if (placement) {
                    const placeholder = placement.components.find((x) => x.placeholder);
                    if (placeholder) {
                        this.viewController.updateComponent(targetPlacementCode, placeholder.code);
                    }
                }
            },

            showLockedToast() {
                this.appStore.showToast({
                    icon: 'info',
                    message: this.$t('GROUP_LOCKED_TOAST_GRIP'),
                    linkText: this.$t('CUSTOMIZE'),
                    linkAction: () => {
                        this.appStore.setShowGroupOverlay(false);
                    },
                });
            },

            selectionIsLocked(placementCode, optionCode) {
                const placement = this.viewController.findPlacement(placementCode);
                const option = placement?.components?.find((component) => component.code === optionCode);

                return this.isLocked && option?.custom?.['v-custom-shop'] === 'true';
            },

            // eslint-disable-next-line no-unused-vars
            selectActual({ placement, option }) {
                if (this.selectionIsLocked(placement, option)) {
                    this.showLockedToast();
                    return;
                }

                this.viewController.updateComponent(placement, option);
            },
        },
    };
</script>
